import React, { useState, useEffect, useRef } from 'react';

// Extract regId from whatever the QR scanner sends (full URL or plain ID)
const parseRegId = (input) => {
    const trimmed = input.trim();
    try {
        const url = new URL(trimmed);
        const confRegId = url.searchParams.get('conf-reg-id');
        if (confRegId) return confRegId.trim();
        const regId = url.searchParams.get('regId');
        if (regId) return regId.trim();
    } catch {}
    const match = trimmed.match(/[?&]conf-reg-id=([A-Z0-9-]+)/i)
        || trimmed.match(/[?&]regId=([A-Z0-9-]+)/i);
    if (match) return match[1].trim();
    return trimmed;
};

const CustomEventAdminScan = () => {
    const [authenticated, setAuthenticated] = useState(false);
    const [credentials, setCredentials] = useState({ username: '', password: '' });
    const [adminRole, setAdminRole] = useState('');

    // Scan state
    const [scanInput, setScanInput] = useState('');
    const [scanning, setScanning] = useState(false);
    const [scanResult, setScanResult] = useState(null);
    const [scanError, setScanError] = useState('');
    const [redeemLoading, setRedeemLoading] = useState(null);

    // Login state
    const [loginForm, setLoginForm] = useState({ username: '', password: '' });
    const [loginLoading, setLoginLoading] = useState(false);
    const [loginError, setLoginError] = useState('');

    const inputRef = useRef(null);

    const urlRegId = new URLSearchParams(window.location.search).get('conf-reg-id') || '';

    useEffect(() => {
        const storedAuth = localStorage.getItem('kapconAdminAuth');
        if (storedAuth) {
            try {
                const parsed = JSON.parse(storedAuth);
                setCredentials({ username: parsed.username, password: parsed.password });
                setAdminRole(parsed.role || '');
                setAuthenticated(true);
            } catch {}
        }
    }, []);

    // Auto-scan from URL param once authenticated
    useEffect(() => {
        if (authenticated && urlRegId) {
            handleScan(urlRegId);
        }
    }, [authenticated]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-focus scan input after each result (only when no URL param)
    useEffect(() => {
        if (authenticated && !urlRegId) {
            setTimeout(() => inputRef.current?.focus(), 150);
        }
    }, [authenticated, scanResult]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoginError('');
        setLoginLoading(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/custom-events/admin/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(loginForm)
            });
            const data = await res.json();
            if (data.success) {
                localStorage.setItem('kapconAdminAuth', JSON.stringify({ username: loginForm.username, password: loginForm.password, role: data.role }));
                setCredentials({ username: loginForm.username, password: loginForm.password });
                setAdminRole(data.role || '');
                setAuthenticated(true);
            } else {
                setLoginError(data.message || 'Invalid credentials');
            }
        } catch { setLoginError('Error connecting to server'); }
        finally { setLoginLoading(false); }
    };

    const handleLogout = () => {
        localStorage.removeItem('kapconAdminAuth');
        setAuthenticated(false);
        setScanResult(null);
        setScanError('');
    };

    const handleScan = async (rawInput) => {
        const regId = parseRegId(rawInput);
        if (!regId) return;

        setScanError('');
        setScanResult(null);
        setScanning(true);
        setScanInput('');

        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/custom-events/admin/scan`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...credentials, regId })
            });
            const data = await res.json();
            if (data.success) {
                setScanResult(data);
            } else {
                setScanError(data.message || 'Participant not found');
            }
        } catch { setScanError('Error connecting to server'); }
        finally { setScanning(false); }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && scanInput.trim()) {
            handleScan(scanInput);
        }
    };

    const handleRedeemToggle = async (amenityId, currentlyRedeemed) => {
        const newRedeemed = !currentlyRedeemed;

        if (!newRedeemed && adminRole !== 'full') {
            alert('Only full admin can undo amenity redemption');
            return;
        }

        setRedeemLoading(amenityId);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/custom-events/admin/amenity/redeem`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...credentials,
                    regId: scanResult.participant.registrationId,
                    amenityId,
                    redeemed: newRedeemed
                })
            });
            const data = await res.json();
            if (data.success) {
                setScanResult(prev => ({
                    ...prev,
                    participant: { ...prev.participant, amenities: data.amenities }
                }));
            } else {
                alert(data.message || 'Failed to update amenity');
            }
        } catch { alert('Error connecting to server'); }
        finally { setRedeemLoading(null); }
    };

    // ---- Login screen ----
    if (!authenticated) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center px-4">
                <div className="w-full max-w-sm">
                    <div className="text-center mb-8">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#5928ed] mb-4">
                            <span className="text-white text-2xl">📷</span>
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900">KAPCON 2026</h1>
                        <p className="text-sm text-gray-500 mt-1">Admin Check-in & Scan</p>
                    </div>
                    <div className="bg-white rounded-2xl shadow-md p-6">
                        <form onSubmit={handleLogin} className="space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Username</label>
                                <input type="text" required value={loginForm.username}
                                    onChange={e => setLoginForm(p => ({ ...p, username: e.target.value }))}
                                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#5928ed]" />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Password</label>
                                <input type="password" required value={loginForm.password}
                                    onChange={e => setLoginForm(p => ({ ...p, password: e.target.value }))}
                                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#5928ed]" />
                            </div>
                            {loginError && <p className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg">{loginError}</p>}
                            <button type="submit" disabled={loginLoading}
                                className="w-full py-2.5 bg-[#5928ed] text-white font-bold text-sm rounded-lg hover:bg-purple-800 disabled:opacity-60 transition-colors">
                                {loginLoading ? 'Logging in...' : 'Log In'}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        );
    }

    // ---- Main scan interface ----
    return (
        <div className="min-h-screen bg-gray-50">
            {/* Top bar */}
            <div className="bg-[#5928ed] text-white px-4 py-3 flex items-center justify-between shadow-md">
                <div className="flex items-center gap-2">
                    <span className="text-lg">📷</span>
                    <div>
                        <div className="font-bold text-sm leading-tight">KAPCON 2026 — Check-in</div>
                        <div className="text-[10px] text-purple-200 font-medium">{credentials.username} · {adminRole === 'full' ? 'Full Admin' : 'Check-in'}</div>
                    </div>
                </div>
                <button onClick={handleLogout} className="text-[11px] text-purple-200 hover:text-white font-medium">Logout</button>
            </div>

            <div className="max-w-lg mx-auto px-4 py-6 space-y-4">

                {/* Scan Input */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">
                        Scan QR Code or Enter Reg ID
                    </label>
                    <div className="flex gap-2">
                        <input
                            ref={inputRef}
                            type="text"
                            value={scanInput}
                            onChange={e => setScanInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Point scanner or type Reg ID → press Enter"
                            autoComplete="off"
                            className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl text-sm font-mono focus:outline-none focus:border-[#5928ed] transition-colors"
                        />
                        <button
                            onClick={() => scanInput.trim() && handleScan(scanInput)}
                            disabled={scanning || !scanInput.trim()}
                            className="px-4 py-3 bg-[#5928ed] text-white text-sm font-bold rounded-xl hover:bg-purple-800 disabled:opacity-50 transition-colors">
                            {scanning ? '⏳' : '→'}
                        </button>
                    </div>
                    <p className="text-[10px] text-gray-400 mt-2">Hardware QR scanner types into this field automatically. Press Enter or tap → to process.</p>
                </div>

                {/* Error */}
                {scanError && (
                    <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-2">
                        <span className="text-red-500 text-lg">⚠️</span>
                        <p className="text-sm text-red-700 font-medium">{scanError}</p>
                    </div>
                )}

                {/* Scanning indicator */}
                {scanning && (
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex items-center justify-center gap-3">
                        <div className="animate-spin w-5 h-5 border-2 border-[#5928ed] border-t-transparent rounded-full"></div>
                        <span className="text-sm text-gray-500 font-medium">Looking up participant...</span>
                    </div>
                )}

                {/* Scan Result */}
                {scanResult && (
                    <div className={`bg-white rounded-2xl shadow-sm border-2 overflow-hidden transition-all ${scanResult.participant.isCheckedIn ? 'border-green-400' : 'border-gray-200'}`}>

                        {/* Auto check-in toast */}
                        {scanResult.autoCheckedIn && (
                            <div className="bg-green-500 text-white px-4 py-2 flex items-center gap-2">
                                <span className="text-base">✅</span>
                                <span className="text-sm font-bold">Automatically Checked In!</span>
                            </div>
                        )}
                        {!scanResult.autoCheckedIn && (
                            <div className="bg-blue-50 border-b border-blue-100 px-4 py-2 flex items-center gap-2">
                                <span className="text-base">ℹ️</span>
                                <span className="text-xs text-blue-700 font-medium">Already checked in previously</span>
                            </div>
                        )}

                        {/* Participant Info */}
                        <div className="p-5">
                            <div className="flex items-start justify-between gap-3 mb-4">
                                <div>
                                    <h2 className="text-xl font-bold text-gray-900 leading-tight">{scanResult.participant.fullName}</h2>
                                    <p className="text-xs font-mono text-gray-500 mt-0.5">{scanResult.participant.registrationId}</p>
                                </div>
                                <span className={`shrink-0 px-2.5 py-1 text-[10px] font-bold rounded-full border ${
                                    scanResult.participant.paymentStatus === 'completed'
                                        ? 'bg-green-100 text-green-800 border-green-200'
                                        : 'bg-yellow-100 text-yellow-800 border-yellow-200'
                                }`}>
                                    {(scanResult.participant.paymentStatus || '').toUpperCase()}
                                </span>
                            </div>

                            <div className="grid grid-cols-2 gap-2 text-xs mb-5 bg-gray-50 p-3 rounded-xl">
                                <div>
                                    <span className="text-gray-400 font-semibold uppercase tracking-wide text-[9px]">Category</span>
                                    <p className="font-semibold text-gray-800 mt-0.5">{scanResult.participant.registrationCategory || '—'}</p>
                                </div>
                                <div>
                                    <span className="text-gray-400 font-semibold uppercase tracking-wide text-[9px]">Designation</span>
                                    <p className="font-semibold text-gray-800 mt-0.5">{scanResult.participant.designation || '—'}</p>
                                </div>
                                {scanResult.participant.organization && (
                                    <div className="col-span-2">
                                        <span className="text-gray-400 font-semibold uppercase tracking-wide text-[9px]">Organisation</span>
                                        <p className="font-semibold text-gray-800 mt-0.5">{scanResult.participant.organization}</p>
                                    </div>
                                )}
                            </div>

                            {/* Amenities */}
                            {scanResult.amenities && scanResult.amenities.length > 0 && (
                                <div>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">Amenities</p>
                                    <div className="space-y-2">
                                        {scanResult.amenities.map(amenity => {
                                            const state = scanResult.participant.amenities?.[amenity._id];
                                            const isRedeemed = state?.redeemed === true;
                                            const isLoading = redeemLoading === amenity._id;
                                            const canUndo = adminRole === 'full';

                                            return (
                                                <div key={amenity._id}
                                                    className={`flex items-center justify-between px-4 py-3 rounded-xl border transition-all ${
                                                        isRedeemed
                                                            ? 'bg-green-50 border-green-300'
                                                            : 'bg-gray-50 border-gray-200'
                                                    }`}>
                                                    <div className="flex items-center gap-3">
                                                        <span className={`text-xl ${isRedeemed ? 'grayscale-0' : 'grayscale opacity-40'}`}>
                                                            {isRedeemed ? '✅' : '⬜'}
                                                        </span>
                                                        <div>
                                                            <p className={`text-sm font-bold ${isRedeemed ? 'text-green-800' : 'text-gray-700'}`}>
                                                                {amenity.name}
                                                            </p>
                                                            {isRedeemed && state?.at && (
                                                                <p className="text-[10px] text-green-600 mt-0.5">
                                                                    {new Date(state.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                                    {state.by && ` · by ${state.by}`}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => handleRedeemToggle(amenity._id, isRedeemed)}
                                                        disabled={isLoading || (isRedeemed && !canUndo)}
                                                        title={isRedeemed && !canUndo ? 'Only full admin can undo' : undefined}
                                                        className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                                                            isRedeemed
                                                                ? canUndo
                                                                    ? 'bg-white border border-red-200 text-red-600 hover:bg-red-50'
                                                                    : 'bg-white border border-gray-200 text-gray-400 cursor-not-allowed'
                                                                : 'bg-[#5928ed] text-white hover:bg-purple-800'
                                                        }`}>
                                                        {isLoading ? '⏳' : isRedeemed ? (canUndo ? 'Undo' : '🔒') : 'Mark'}
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {scanResult.amenities && scanResult.amenities.length === 0 && (
                                <div className="text-center py-4 text-gray-400 text-xs">No amenities configured for this event type.</div>
                            )}
                        </div>

                        {/* Footer: scan next */}
                        <div className="px-5 pb-4">
                            <button
                                onClick={() => { setScanResult(null); setScanError(''); setTimeout(() => inputRef.current?.focus(), 50); }}
                                className="w-full py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-bold rounded-xl transition-colors">
                                ← Scan Next Participant
                            </button>
                        </div>
                    </div>
                )}

                {/* Empty state */}
                {!scanResult && !scanning && !scanError && (
                    <div className="text-center py-12 text-gray-300">
                        <div className="text-6xl mb-4">📷</div>
                        <p className="text-sm font-medium">Ready to scan</p>
                        <p className="text-xs mt-1">Point a QR scanner or enter a Reg ID above</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CustomEventAdminScan;
